import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface OversightState {
  data: any;
  loading: boolean;
  waiting: boolean;
  errors: string[];
  options: any;
  date_range: {
    start: string | null;
    end: string | null;
  };
}

// Define the initial state using that type
const initialState: OversightState = {
  data: null,
  loading: false,
  waiting: false,
  errors: [],
  options: {},
  date_range: {
    start: null,
    end: null,
  },
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get oversight overview
export const getOverview = createAsyncThunk(
  "oversight/overview/get",
  async (
    obj: {
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/overview/`;
      if (obj.dateRange) url += `?dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight feed
export const getFeed = createAsyncThunk(
  "oversight/feed/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/feed/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight communication
export const getCommunication = createAsyncThunk(
  "oversight/communication/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/communication/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight devices
export const getDevices = createAsyncThunk(
  "oversight/devices/get",
  async (
    obj: {
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/devices/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight content
export const getContent = createAsyncThunk(
  "oversight/content/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/content/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight log
export const getLog = createAsyncThunk(
  "oversight/log/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/log/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight orientation
export const getOrientation = createAsyncThunk(
  "oversight/orientation/get",
  async (
    obj: {
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/orientation/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight integration
export const getIntegration = createAsyncThunk(
  "oversight/integration/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/integration/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight smart control
export const getSmartControl = createAsyncThunk(
  "oversight/smart-control/get",
  async (
    obj: {
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/smart-control/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight tasks
export const getTasks = createAsyncThunk(
  "oversight/tasks/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/tasks/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get oversight users
export const getUsers = createAsyncThunk(
  "oversight/users/get",
  async (
    obj: {
      dateRange: string | null;
      propertyId: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/oversight/users/`;

      // Send request
      const res = await axios.get(url, {
        params: {
          dateRange: obj.dateRange,
          property: obj.propertyId,
        },
        ...config,
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const oversightSlice = createSlice({
  name: "oversight",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get oversight overview
      .addCase(getOverview.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getOverview.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getOverview.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight feed
      .addCase(getFeed.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getFeed.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getFeed.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight communication
      .addCase(getCommunication.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getCommunication.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getCommunication.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight devices
      .addCase(getDevices.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getDevices.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getDevices.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight content
      .addCase(getContent.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getContent.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getContent.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight log
      .addCase(getLog.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getLog.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getLog.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight orientation
      .addCase(getOrientation.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getOrientation.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getOrientation.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight integration
      .addCase(getIntegration.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getIntegration.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getIntegration.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight smart control
      .addCase(getSmartControl.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getSmartControl.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getSmartControl.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight tasks
      .addCase(getTasks.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getTasks.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      })

      // Get oversight users
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.waiting = false;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.date_range.start = action.payload.data.start_date;
        state.date_range.end = action.payload.data.end_date;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(getUsers.rejected, (state) => {
        state.loading = false;
        state.waiting = false;
      });
  },
});

export default oversightSlice.reducer;
